import { useState, useRef, useCallback, useEffect } from 'react';
import useAppContext from '../hooks/useAppContext';
import useAcceptStudioContract from '../hooks/useAcceptStudioContract';
import useContractContent from '../hooks/useContractContent';
import { AlertType } from '../design-system/DSAlert';
import DSBox from '../design-system/DSBox';
import { DSTitleTypography } from '../design-system/Typography';
import DSCheckbox from '../design-system/DSCheckBox';
import DSFormControlLabel from '../design-system/DSFormControlLabel';
import DSButton from '../design-system/DSButton';
import Contract from './Contract';
import { useTheme } from '@mui/material';

type ContractAgreementFormProps = {
  onAccepted?: () => void;
};

const ContractAgreementForm = ({ onAccepted }: ContractAgreementFormProps) => {
  const { setUser, user, setHasAgreedToContract, showAlert } = useAppContext();
  const { spacing } = useTheme();
  const [agreed, setAgreed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Shares the ['contractContent'] query with the <Contract /> child —
  // no extra request, just visibility into when the content has rendered
  const { data: contractContent } = useContractContent();

  // The scroll gate must also open when the contract doesn't overflow the
  // container (short content, tall viewport) — onScroll alone never fires
  // in that case and the checkbox would stay disabled forever
  useEffect(() => {
    if (!contractContent?.html) return;
    const el = scrollRef.current;
    if (!el) return;
    const evaluate = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
        setScrolledToBottom(true);
      }
    };
    evaluate();
    const resizeObserver = new ResizeObserver(evaluate);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [contractContent?.html]);

  const { mutate: acceptContract, isPending } = useAcceptStudioContract(
    user?.id ?? '',
    {
      onSuccess: (updatedUser) => {
        setUser((prevUser) => ({
          ...prevUser,
          ...updatedUser,
        }));
        setHasAgreedToContract?.(true);
        onAccepted?.();
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message;
        showAlert({
          message: errorMessage,
          alertType: AlertType.ERROR,
        });
      },
    },
  );

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setScrolledToBottom(true);
      }
    }
  };

  const handleAgree = useCallback(() => {
    if (agreed && user && !isPending) {
      acceptContract();
    }
  }, [agreed, user, isPending, acceptContract]);

  const handleAgreeStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAgreed(e.target.checked);
    },
    [],
  );

  return (
    <>
      <DSTitleTypography gutterBottom>
        ARTIST COLLABORATION TERMS AND CONDITIONS
      </DSTitleTypography>

      <DSBox
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: '100%',
          overflowY: 'auto',
          border: '1px solid #ccc',
          padding: spacing(2),
          borderRadius: spacing(1),
        }}
      >
        <Contract />
      </DSBox>

      <DSFormControlLabel
        control={
          <DSCheckbox
            checked={agreed}
            onChange={handleAgreeStateChange}
            disabled={!scrolledToBottom}
          />
        }
        label="I agree"
      />

      {/* Also disabled while the request is in flight: signing opens a
          CollabPay collaborator, and a slow CollabPay used to leave this
          button live for minutes — every extra click fired another signing
          request against the same account */}
      <DSButton
        onClick={handleAgree}
        disabled={!agreed || isPending}
        variant="contained"
        fullWidth
      >
        {isPending ? 'Signing…' : 'Agree & Continue'}
      </DSButton>
    </>
  );
};

export default ContractAgreementForm;
